import { CodeBlock, InfoBox, SectionTitle, SubsectionTitle, Table } from './shared';

export function SandboxContent() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-white mb-4">沙箱隔离</h1>
        <p className="text-lg text-slate-400 leading-relaxed">
          OpenClaw 可以在 Docker 容器内运行工具以减少影响范围。可选的，由配置控制。
        </p>
      </div>

      <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5 space-y-2 text-slate-300 text-sm leading-relaxed">
        <p>如果沙箱隔离关闭，工具在主机上运行。</p>
        <p>Gateway 网关保留在主机上；启用时工具执行在隔离的沙箱中运行。</p>
        <p className="text-slate-400">不是完美的安全边界，但实质性地限制了文件系统和进程访问。</p>
      </div>

      <SectionTitle id="what-is-sandboxed">什么会被沙箱隔离</SectionTitle>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-slate-900/50 border border-green-500/20 rounded-xl p-5">
          <h3 className="text-green-400 font-semibold mb-3 flex items-center gap-2">
            <i className="ri-shield-check-line"></i>
            会被沙箱隔离
          </h3>
          <ul className="space-y-2 text-slate-300 text-sm">
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-green-400 mt-0.5 flex-shrink-0"></i>
              <span>工具执行（<code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">exec</code>、<code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">read</code>、<code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">write</code>、<code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">edit</code>、<code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">apply_patch</code>、<code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">process</code> 等）</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-green-400 mt-0.5 flex-shrink-0"></i>
              <span>可选的沙箱浏览器（<code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">agents.defaults.sandbox.browser</code>）</span>
            </li>
          </ul>
        </div>
        <div className="bg-slate-900/50 border border-red-500/20 rounded-xl p-5">
          <h3 className="text-red-400 font-semibold mb-3 flex items-center gap-2">
            <i className="ri-shield-cross-line"></i>
            不被沙箱隔离
          </h3>
          <ul className="space-y-2 text-slate-300 text-sm">
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-red-400 mt-0.5 flex-shrink-0"></i>
              <span>Gateway 网关进程本身</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-red-400 mt-0.5 flex-shrink-0"></i>
              <span>任何明确允许在主机上运行的工具（例如 <code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">tools.elevated</code>）</span>
            </li>
          </ul>
        </div>
      </div>

      <SectionTitle id="modes">模式（<code className="text-cyan-400 font-mono text-xl">agents.defaults.sandbox.mode</code>）</SectionTitle>
      <Table
        headers={['值', '说明']}
        rows={[
          [<code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">"off"</code>, '不使用沙箱隔离'],
          [<code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">"non-main"</code>, '仅沙箱隔离非主会话（默认值）'],
          [<code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">"all"</code>, '每个会话都在沙箱中运行'],
        ]}
      />

      <SectionTitle id="scope">作用域（<code className="text-cyan-400 font-mono text-xl">agents.defaults.sandbox.scope</code>）</SectionTitle>
      <Table
        headers={['值', '说明']}
        rows={[
          [<code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">"session"</code>, '每个会话一个容器（默认）'],
          [<code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">"agent"</code>, '每个智能体一个容器'],
          [<code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">"shared"</code>, '所有沙箱会话共享一个容器'],
        ]}
      />

      <SectionTitle id="workspace-access">工作区访问（<code className="text-cyan-400 font-mono text-xl">agents.defaults.sandbox.workspaceAccess</code>）</SectionTitle>
      <Table
        headers={['值', '说明']}
        rows={[
          [<code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">"none"</code>, '工具看到 ~/.openclaw/sandboxes 下的沙箱工作区（默认）'],
          [<code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">"ro"</code>, '以只读方式在 /agent 挂载智能体工作区'],
          [<code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">"rw"</code>, '以读写方式在 /workspace 挂载智能体工作区'],
        ]}
      />

      <SectionTitle id="custom-binds">自定义绑定挂载（<code className="text-cyan-400 font-mono text-xl">agents.defaults.sandbox.docker.binds</code>）</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        格式：<code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">host:container:mode</code>（例如 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">"/home/user/source:/source:rw"</code>）
      </p>
      <InfoBox type="info" title="合并规则">
        全局和每智能体的绑定是合并的（不是替换）。
      </InfoBox>
      <CodeBlock
        language="json5"
        code={`{
  agents: {
    defaults: {
      sandbox: {
        docker: {
          binds: ["/home/user/source:/source:ro", "/var/run/docker.sock:/var/run/docker.sock"],
        },
      },
    },
    list: [
      {
        id: "build",
        sandbox: {
          docker: {
            binds: ["/mnt/cache:/cache:rw"],
          },
        },
      },
    ],
  },
}`}
      />

      <SectionTitle id="image-setup">镜像 + 设置</SectionTitle>
      <div className="space-y-3 text-slate-300 leading-relaxed">
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4 space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-slate-400 w-28 flex-shrink-0">默认镜像：</span>
            <code className="text-cyan-400">openclaw-sandbox:bookworm-slim</code>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 w-28 flex-shrink-0">构建命令：</span>
            <code className="text-cyan-400">scripts/sandbox-setup.sh</code>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 w-28 flex-shrink-0">沙箱浏览器：</span>
            <code className="text-cyan-400">scripts/sandbox-browser-setup.sh</code>
          </div>
        </div>
        <p>默认情况下，沙箱容器运行时没有网络。通过 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">agents.defaults.sandbox.docker.network</code> 覆盖。</p>
      </div>

      <SectionTitle id="setup-command">setupCommand（一次性容器设置）</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        在沙箱容器创建后运行一次（不是每次运行），通过 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">sh -lc</code> 在容器内执行。
      </p>
      <div className="space-y-2 text-sm text-slate-300 mb-4">
        <p>路径：</p>
        <ul className="ml-4 space-y-1">
          <li><code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">agents.defaults.sandbox.docker.setupCommand</code></li>
          <li><code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">agents.list[].sandbox.docker.setupCommand</code></li>
        </ul>
      </div>
      <InfoBox type="warning" title="常见陷阱">
        <ul className="space-y-1 mt-1">
          <li>• 默认 <code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">docker.network</code> 是 <code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">"none"</code>（无出口）</li>
          <li>• <code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">readOnlyRoot: true</code> 阻止写入</li>
          <li>• <code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">user</code> 必须是 root 才能安装包</li>
        </ul>
      </InfoBox>

      <SectionTitle id="tool-policy">工具策略 + 逃逸通道</SectionTitle>
      <div className="space-y-3 text-slate-300 leading-relaxed">
        <p>工具允许/拒绝策略仍在沙箱规则之前应用。</p>
        <p>
          <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">tools.elevated</code> 是一个显式的逃逸通道，在主机上运行 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">exec</code>。
        </p>
        <p>
          调试：使用 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">openclaw sandbox explain</code> 检查生效的沙箱模式。
        </p>
      </div>

      <SectionTitle id="multi-agent">多智能体覆盖</SectionTitle>
      <p className="text-slate-300 leading-relaxed">
        每个智能体可以覆盖沙箱 + 工具：<code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">agents.list[].sandbox</code> 和 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">agents.list[].tools</code>。
      </p>

      <SectionTitle id="minimal-example">最小启用示例</SectionTitle>
      <CodeBlock
        language="json5"
        code={`{
  agents: {
    defaults: {
      sandbox: {
        mode: "non-main",
        scope: "session",
        workspaceAccess: "none",
      },
    },
  },
}`}
      />

      <SectionTitle id="related-docs">相关文档</SectionTitle>
      <div className="space-y-2">
        {[
          { href: '/gateway/configuration.html#agentsdefaults-sandbox', label: '沙箱配置' },
          { href: '/tools/multi-agent-sandbox-tools.html', label: '多智能体沙箱与工具' },
          { href: '/gateway/security.html', label: '安全' },
        ].map((link) => (
          <a
            key={link.href}
            href={link.href}
            className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors text-sm"
          >
            <i className="ri-arrow-right-s-line"></i>
            {link.label}
          </a>
        ))}
      </div>
    </div>
  );
}

export default SandboxContent;
